import { h } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";
import { button, toast } from "@/components/ui";
import { auth, timetableService, subjectsService } from "@/services";
import { topicsService } from "@/services/topics.service";
import { currentSlot, buildBlocks, weekdayOf, nowPosition, addDays, toMinutes, type SlotBlock } from "@/features/timetable/schedule";
import { navigate, type RouteContext } from "@/app/router";
import { getTheme, toggleTheme } from "@/lib/theme";
import { subjectIcon } from "@/lib/subjectIcons";
import { defaultColor, contrastText } from "@/lib/colors";
import { config } from "@/config";
import { offlineSync } from "@/services/offline/offline-sync.service";
import { openSyncIssuesDialog } from "@/features/offline/syncIssuesDialog";
import { localFiles } from "@/services/document/localFiles";
import type { Period, Subject, TimetableSlot, Weekday } from "@/types/models";

/** Fach-Box mit laufender/nächster Stunde in der Kopfzeile zeigen. */
const SHOW_LESSON_BADGE = false;

/**
 * Klick auf den App-Namen: läuft gerade eine Stunde laut Stundenplan, geht es
 * direkt ins aktuelle Thema dieses Fachs – sonst zur Übersicht. Scheitert
 * irgendein Schritt (kein Stundenplan, kein Thema, offline), landet man
 * einfach auf der Startseite statt mit einem Fehler dazustehen.
 */
async function goHomeOrCurrentLesson(): Promise<void> {
  try {
    const [settings, slots] = await Promise.all([timetableService.getSettings(), timetableService.listSlots()]);
    const hit = currentSlot(slots, settings.periods);
    if (hit) {
      const subject = (await subjectsService.list()).find((s) => s.id === hit.slot.subject);
      if (subject) {
        const topic = await topicsService.currentBySubject(subject.id);
        if (topic) {
          navigate(`/topic/${topic.id}`);
          return;
        }
      }
    }
  } catch {
    /* fällt unten auf die Übersicht zurück */
  }
  navigate("/");
}

interface Hit {
  block: SlotBlock;
  weekday: Weekday;
}

/**
 * Laufende Stunde UND die direkt darauffolgende – in der Pause zeigt "next"
 * schon die kommende, nach Schulschluss die erste Stunde des nächsten
 * Schultags (Freitagnachmittag -> Montag, 1. Stunde), an unterrichtsfreien
 * Tagen entsprechend weiter übersprungen. `current` ist nur gesetzt, wenn
 * WIRKLICH gerade eine Stunde läuft – in der Pause bleibt es null, "next"
 * übernimmt dann allein.
 */
function findCurrentAndNext(
  slots: TimetableSlot[],
  periods: Period[],
  days: Weekday[],
  now = new Date()
): { current: Hit | null; next: Hit | null } {
  const pos = nowPosition(periods, now);
  const today = weekdayOf(now);

  let current: Hit | null = null;
  if (pos.period !== null && days.includes(today)) {
    const cur = buildBlocks(slots, today, periods.length).find(
      (b) => pos.period! >= b.from && pos.period! < b.from + b.span
    );
    if (cur) current = { block: cur, weekday: today };
  }

  let next: Hit | null = null;
  for (let d = 0; d <= 7 && !next; d++) {
    const wd = weekdayOf(addDays(now, d));
    if (!days.includes(wd)) continue;
    const blocks = buildBlocks(slots, wd, periods.length).sort((a, b) => a.from - b.from);
    for (const block of blocks) {
      if (d === 0) {
        if (current && wd === today) {
          // Nach dem Ende der laufenden Stunde suchen, nicht nach der Uhrzeit –
          // sonst zählt die laufende Stunde selbst als "nächste".
          if (block.from < current.block.from + current.block.span) continue;
        } else {
          const startMin = toMinutes(periods[block.from - 1]?.start ?? "");
          if (startMin === null || startMin <= pos.minutes) continue; // schon begonnen/vorbei
        }
      }
      next = { block, weekday: wd };
      break;
    }
  }
  return { current, next };
}

/**
 * Fach-Boxen in der Kopfzeile: laufende Stunde, Pfeil, nächste Stunde – 1:1
 * im Design der einstündigen Stundenplan-Box (`.tt-block`), nur etwas größer
 * (siehe `.lesson-badge` in v1.css) und ohne das Grid-Positionieren, das dort
 * fürs Stundenraster gebraucht wird. Läuft gerade nichts (Pause, vor/nach der
 * Schule), bleibt die linke Box weg und nur "als Nächstes" steht da. Klick
 * führt direkt ins jeweilige Thema.
 *
 * Läuft alle 30 s neu, damit Pausenende/Stundenwechsel ohne Neuladen der
 * Seite ankommen; stoppt automatisch, sobald die Navbar aus dem DOM fällt.
 * Scheitert ein Durchlauf (kurzer Netzwerkhänger), wird es NICHT bis zum
 * nächsten 30-s-Takt leer gelassen, sondern nach 3 s nachgeholt – sonst kann
 * die Box direkt nach dem Login bis zu 30 s lang grundlos leer bleiben.
 */
function fillLessonBadge(slot: HTMLElement): void {
  let timer = 0;

  const lessonBox = (hit: Hit, isCurrent: boolean, subjects: Subject[], periods: Period[]) => {
    const subject = subjects.find((s) => s.id === hit.block.subject);
    if (!subject) return null;
    const to = hit.block.from + hit.block.span - 1;
    const timeLabel = `${periods[hit.block.from - 1]?.start}–${periods[to - 1]?.end}`;
    const accent = subject.color || defaultColor();
    return h(
      "button",
      {
        type: "button",
        class: "tt-block lesson-badge" + (isCurrent ? "" : " lesson-badge-next"),
        style: `--accent:${accent};--on-accent:${contrastText(accent)}`,
        title: `${subject.title} · ${timeLabel}${isCurrent ? " (läuft)" : " (als Nächstes)"}`,
        onclick: async () => {
          const topic = await topicsService.currentBySubject(subject.id).catch(() => undefined);
          navigate(topic ? `/topic/${topic.id}` : "/");
        },
      },
      h("span", { class: "b-icon", html: subjectIcon(subject.icon, 15) }),
      h(
        "span",
        { class: "b-body" },
        h("span", { class: "b-title" }, subject.title),
        !isCurrent ? h("span", { class: "b-exam" }, "als Nächstes") : null
      ),
      hit.block.room ? h("span", { class: "b-room" }, hit.block.room) : null
    );
  };

  const paint = async () => {
    if (!slot.isConnected) {
      window.clearInterval(timer);
      return;
    }
    try {
      const [settings, slots, subjects] = await Promise.all([
        timetableService.getSettings(),
        timetableService.listSlots(),
        subjectsService.list(),
      ]);
      const { current, next } = findCurrentAndNext(slots, settings.periods, settings.days);
      slot.innerHTML = "";
      const currentEl = current ? lessonBox(current, true, subjects, settings.periods) : null;
      const nextEl = next ? lessonBox(next, false, subjects, settings.periods) : null;
      if (currentEl) slot.appendChild(currentEl);
      if (currentEl && nextEl) slot.appendChild(h("span", { class: "lesson-badge-arrow", html: icon("chevron-right", 16) }));
      if (nextEl) slot.appendChild(nextEl);
    } catch {
      window.setTimeout(() => void paint(), 3000);
    }
  };

  void paint();
  timer = window.setInterval(() => void paint(), 30_000);
}

interface NavItem {
  href: string;
  icon: IconName;
  label: string;
}

/**
 * Sichtbare Tabs. Suche und Statistik sind vorerst ausgeblendet – die Routen
 * bleiben erreichbar, tauchen aber nicht mehr in der Navigation auf.
 */
const NAV: NavItem[] = [
  { href: "/", icon: "home", label: "Start" },
  { href: "/timetable", icon: "clock", label: "Stundenplan" },
  { href: "/homework", icon: "checklist", label: "Hausaufgaben" },
  { href: "/exams", icon: "calendar", label: "Prüfungen" },
];

/**
 * Das Theme lässt sich auch in den Einstellungen umschalten. AppLayout wird bei
 * jedem Routenwechsel neu gebaut – deshalb genau ein globaler Listener, der den
 * jeweils aktuellen Button im DOM nachzieht (statt einer pro Layout-Instanz).
 */
let themeSyncBound = false;
function bindThemeIconSync(): void {
  if (themeSyncBound) return;
  themeSyncBound = true;
  window.addEventListener("app:themechange", () => {
    const slot = document.querySelector(".app-navbar .theme-btn .row");
    if (slot) slot.innerHTML = icon(getTheme() === "dark" ? "sun" : "moon", 18);
  });
}

/**
 * Kreis-Pfeile-Knopf neben dem Theme-Schalter: nur sichtbar, solange offline
 * entstandene Änderungen (Notizen/Arbeits-PDF, siehe offline-sync.service.ts)
 * noch nicht hochgeladen sind. Selber Grund für den globalen Listener wie bei
 * `bindThemeIconSync` – AppLayout wird pro Route neu gebaut.
 */
let syncIconBound = false;
let syncing = false;
function bindSyncIconRefresh(): void {
  if (syncIconBound) return;
  syncIconBound = true;
  window.addEventListener("online", () => void refreshSyncButton());
  // Lokal gespeicherte Materialdateien (localFiles.ts) kommen und gehen ohne
  // Seitenwechsel – der Knopf soll das sofort zeigen.
  localFiles.onChange(() => void refreshSyncButton());
  void refreshSyncButton();
}

/**
 * Sichtbares "Offline"-Zeichen neben den anderen Knöpfen – unabhängig vom
 * Sync-Knopf (der nur erscheint, wenn es tatsächlich etwas hochzuladen
 * gibt). Ohne das merkt man am Gerät gar nicht, dass die App den fehlenden
 * Netzzugang überhaupt bemerkt hat.
 */
let offlineIndicatorBound = false;
function bindOfflineIndicator(): void {
  if (offlineIndicatorBound) return;
  offlineIndicatorBound = true;
  window.addEventListener("online", updateOfflineIndicator);
  window.addEventListener("offline", updateOfflineIndicator);
}
function updateOfflineIndicator(): void {
  const el = document.querySelector<HTMLElement>(".app-navbar .offline-indicator");
  if (el) el.style.display = navigator.onLine ? "none" : "";
}

async function refreshSyncButton(): Promise<void> {
  const btn = document.querySelector<HTMLElement>(".app-navbar .sync-btn");
  if (!btn) return;
  const pending = await offlineSync.hasPending().catch(() => false);
  btn.style.display = pending ? "" : "none";
}

async function runSync(btn: HTMLElement): Promise<void> {
  if (syncing) return;
  if (!navigator.onLine) {
    // Kein Internet, aber vielleicht liegen schon Probleme vom letzten Mal
    // vor, die sich auch offline ansehen/verwerfen lassen.
    const issues = await offlineSync.loadIssues();
    if (issues.length) return void openSyncIssuesDialog();
    return toast("Noch kein Internet.", "error");
  }
  syncing = true;
  btn.classList.add("is-spinning");
  try {
    const result = await offlineSync.sync();
    if (result.synced) toast(`${result.synced} Änderung${result.synced === 1 ? "" : "en"} synchronisiert.`, "success");
    const issues = await offlineSync.loadIssues();
    if (issues.length) {
      toast(`${issues.length} Problem${issues.length === 1 ? "" : "e"} bei der Synchronisierung – bitte prüfen.`, "error");
      await openSyncIssuesDialog();
    } else if (!result.synced) {
      toast("Alles synchron.", "success");
    }
  } catch (err) {
    toast(err instanceof Error ? err.message : "Sync fehlgeschlagen.", "error");
  } finally {
    syncing = false;
    btn.classList.remove("is-spinning");
    void refreshSyncButton();
  }
}

/** App-Rahmen: Top-Navbar + linke Sidebar (Desktop) + Bottom-Tabs (mobil). */
export function AppLayout(content: HTMLElement, ctx: RouteContext): HTMLElement {
  const isActive = (href: string) =>
    href === "/"
      ? ctx.path === "/" || ctx.path.startsWith("/topic")
      : ctx.path.startsWith(href);

  const offlineIndicator = h(
    "span",
    { class: "offline-indicator", title: "Offline – Änderungen werden zwischengespeichert und später synchronisiert", style: navigator.onLine ? "display:none" : "" },
    h("span", { class: "row", html: icon("wifi-off", 16) }),
    "Offline"
  );
  bindOfflineIndicator();

  const syncBtn = button({
    icon: "refresh-cw",
    variant: "ghost",
    title: "Offline-Änderungen synchronisieren",
    onClick: () => void runSync(syncBtn),
  });
  syncBtn.classList.add("sync-btn");
  syncBtn.style.display = "none";
  bindSyncIconRefresh();
  void refreshSyncButton();

  const themeBtn = button({
    icon: getTheme() === "dark" ? "sun" : "moon",
    variant: "ghost",
    title: "Theme wechseln",
    onClick: () => toggleTheme(),
  });
  themeBtn.classList.add("theme-btn");
  bindThemeIconSync();

  const logoutBtn = button({
    icon: "logout",
    variant: "ghost",
    title: "Abmelden",
    onClick: () => {
      auth.logout();
      navigate("/login");
    },
  });

  // Zwei Abstandshalter links und rechts davon halten die Pille mittig,
  // unabhängig davon, wie breit App-Name und Knöpfe gerade sind.
  const lessonSlot = h("div", { class: "lesson-slot" });

  const navbar = h(
    "header",
    { class: "app-navbar" },
    h(
      "div",
      { class: "app-navbar-content" },
      h(
        "a",
        {
          href: "#/",
          class: "brand",
          onclick: (e: MouseEvent) => {
            e.preventDefault();
            void goHomeOrCurrentLesson();
          },
        },
        h("span", { class: "row", html: icon("book", 22) }),
        config.appName
      ),
      h("span", { class: "navbar-spacer" }),
      lessonSlot,
      h("span", { class: "navbar-spacer" }),
      offlineIndicator,
      syncBtn,
      themeBtn,
      logoutBtn
    )
  );
  if (SHOW_LESSON_BADGE) fillLessonBadge(lessonSlot);

  const sidebar = h(
    "aside",
    { class: "app-sidebar" },
    h(
      "nav",
      { class: "sidebar-nav" },
      ...NAV.map((n) =>
        h(
          "a",
          {
            href: `#${n.href}`,
            class: "sidebar-link" + (isActive(n.href) ? " active" : ""),
          },
          h("span", { class: "si", html: icon(n.icon, 20) }),
          h("span", null, n.label)
        )
      )
    ),
    h("div", { class: "sidebar-footer" }, `${config.appName} · v0.1`)
  );

  const bottomTabs = h(
    "nav",
    { class: "bottom-tabs" },
    ...NAV.map((n) =>
      h(
        "a",
        {
          href: `#${n.href}`,
          class: "bottom-tab" + (isActive(n.href) ? " active" : ""),
        },
        h("span", { class: "tab-icon", html: icon(n.icon, 22) }),
        h("span", null, n.label)
      )
    )
  );

  return h(
    "div",
    { class: "app-shell" },
    navbar,
    h(
      "div",
      { class: "app-layout" },
      sidebar,
      // Die Scrollfläche geht über die volle Breite (Scrollbar sitzt dadurch
      // immer am rechten Bildschirmrand), die Inhaltsbreite begrenzt erst der
      // innere Kasten.
      h("main", { class: "app-content" }, h("div", { class: "app-content-inner" }, content))
    ),
    bottomTabs
  );
}
